import type { ContentReference } from "./brain/retriever";
import type { AccountProvider } from "./company-credentials";

export type MessageToolType =
	| "parent-blocks"
	| "documents"
	| "links"
	| "analysis"
	| "codeBasedTableAnalysis"
	| "deep-diff"
	| "mailbox";

export type MessageToolStepStatus = "queued" | "processing" | "processed";
export type MessageToolStatus = "processing" | "processed";

type MessageToolGenericData<TData extends object> = TData & {
	status: MessageToolStatus;
	statusText?: string;
	contentReferences?: ContentReference[];
	contentReferenceOffset?: number;
	excerptOffset?: number;
};

type MessageToolGeneric<
	TType extends MessageToolType,
	TData extends MessageToolGenericData<object>,
> = {
	type: TType;
	data: TData;
};

export type MessageToolStep = {
	id: string;
	label: string;
	status: MessageToolStepStatus;
};

export type MessageToolDocumentUsage = "retrieved" | "sentToLlm" | "cited";

export type MessageToolDocument = {
	id: string;
	name: string;
	url?: string;
	sourceLabel?: string;
	mimeType?: string;
	usage?: MessageToolDocumentUsage;
};

export type MessageToolDocumentsData = MessageToolGenericData<{
	steps: MessageToolStep[];
	documents: MessageToolDocument[];
}>;

export type MessageToolDocuments = MessageToolGeneric<"documents", MessageToolDocumentsData>;

export type MessageToolLink = {
	id: string;
	url: string;
	favicon?: string;
	content?: string;
};

export type MessageToolLinksData = MessageToolGenericData<{
	steps: MessageToolStep[];
	links: MessageToolLink[];
}>;

export type MessageToolLinks = MessageToolGeneric<"links", MessageToolLinksData>;

export type MessageToolAnalysisDocument = {
	id: string;
	name: string;
	sourceLabel?: string;
	mimeType?: string;
	status: MessageToolStepStatus;
	statusText?: string;
};

export type MessageToolAnalysisData = MessageToolGenericData<{
	documents: MessageToolAnalysisDocument[];
}>;

export type MessageToolAnalysis = MessageToolGeneric<"analysis", MessageToolAnalysisData>;

export type MessageToolCodeBasedTableAnalysisStep = {
	id: string;
	label: string;
	status: MessageToolStepStatus;
	stdout?: string;
	stderr?: string;
	exitCode?: number | null;
	timedOut?: boolean;
	durationMs?: number;
	error?: string;
};

export type MessageToolCodeBasedTableAnalysisDocument = {
	id: string;
	name: string;
	mimeType?: string;
	status: MessageToolStepStatus;
	statusText?: string;
	steps: MessageToolCodeBasedTableAnalysisStep[];
	synthesis?: string;
};

export const CODE_BASED_TABLE_ANALYSIS_NAME_SEPARATOR = " | ";

export type MessageToolCodeBasedTableAnalysisData = MessageToolGenericData<{
	documents: MessageToolCodeBasedTableAnalysisDocument[];
}>;

export type MessageToolCodeBasedTableAnalysis = MessageToolGeneric<
	"codeBasedTableAnalysis",
	MessageToolCodeBasedTableAnalysisData
>;

export type DeepDiffSemanticChunk = {
	sourceChunkText: string;
	targetChunksText: string[];
	llmResult: string;
};

export type MessageToolDeepDiffQuickScanComparison = {
	id: string;
	mode: "quick-scan";
	sourceDocument: { id: string; name: string };
	targetDocument: { id: string; name: string };
	sourceText: string;
	targetText: string;
	diffPercent: number;
};

export type MessageToolDeepDiffSemanticComparison = {
	id: string;
	mode: "semantic-compare";
	sourceDocument: { id: string; name: string };
	targetDocument: { id: string; name: string };
	chunks: DeepDiffSemanticChunk[];
	changeRate: number;
};

export type MessageToolDeepDiffComparison =
	| MessageToolDeepDiffQuickScanComparison
	| MessageToolDeepDiffSemanticComparison;

export type MessageToolDeepDiffData = MessageToolGenericData<{
	steps: MessageToolStep[];
	sourceDocument?: { id: string; name: string };
	targetDocument?: { id: string; name: string };
	comparisons: MessageToolDeepDiffComparison[];
}>;

export type MessageToolDeepDiff = MessageToolGeneric<"deep-diff", MessageToolDeepDiffData>;

export type MessageToolMailboxMessage = {
	id: string;
	subject: string;
	from?: string;
	receivedAt?: string;
	webUrl?: string;
	attachments?: Array<{ name: string }>;
};

export type MessageToolMailboxConnectionReason = "not-connected" | "expired";

export interface MessageToolMailboxConnection {
	provider: AccountProvider;
	reason: MessageToolMailboxConnectionReason;
}

export type MessageToolMailboxData = MessageToolGenericData<{
	steps: MessageToolStep[];
	messages: MessageToolMailboxMessage[];
	connection?: MessageToolMailboxConnection;
}>;

export type MessageToolMailbox = MessageToolGeneric<"mailbox", MessageToolMailboxData>;

export type MessageToolParentBlock = {
	id: string;
	name: string;
	tokenCount: number;
};

export type MessageToolParentBlocksData = MessageToolGenericData<{
	blocks: MessageToolParentBlock[];
}>;

export type MessageToolParentBlocks = MessageToolGeneric<
	"parent-blocks",
	MessageToolParentBlocksData
>;

export type MessageTool =
	| MessageToolParentBlocks
	| MessageToolDocuments
	| MessageToolLinks
	| MessageToolAnalysis
	| MessageToolCodeBasedTableAnalysis
	| MessageToolDeepDiff
	| MessageToolMailbox;

export type MessageToolsRecord = {
	[T in MessageTool as T["type"]]: T["data"];
};

export const mapMessageToolsToMessageToolsRecords = (
	tools: MessageTool[],
): Partial<MessageToolsRecord> => {
	const records = tools.map((tool) => ({
		[tool.type]: tool.data,
	}));

	return records.reduce<Partial<MessageToolsRecord>>(
		(acc, record) => ({ ...acc, ...record }),
		{},
	);
};

export const createDefaultMessageTools = (): MessageToolsRecord => ({
	// eslint-disable-next-line @typescript-eslint/naming-convention
	"parent-blocks": {
		status: "processing",
		blocks: [],
	},
	documents: {
		status: "processing",
		statusText: "",
		steps: [],
		documents: [],
	},
	links: {
		status: "processing",
		statusText: "",
		steps: [],
		links: [],
	},
	analysis: {
		status: "processing",
		statusText: "",
		documents: [],
	},
	codeBasedTableAnalysis: {
		status: "processing",
		statusText: "",
		documents: [],
	},

	// eslint-disable-next-line @typescript-eslint/naming-convention
	"deep-diff": {
		status: "processing",
		statusText: "",
		steps: [],
		comparisons: [],
	},
	mailbox: {
		status: "processing",
		statusText: "",
		steps: [],
		messages: [],
	},
});

// Defined order of contentReference insertion for response
export const MESSAGE_TOOL_ORDER: MessageToolType[] = [
	"parent-blocks",
	"deep-diff",
	"codeBasedTableAnalysis",
	"documents",
	"links",
	"mailbox",
];
